/**
 * PrePublishWarningDialog — a workbench, not a complaint.
 *
 * Three sections (each rendered only if it has rows):
 *  1. FIX — editable metadata rows with band meters + live re-evaluation
 *  2. CONFIRM — human-verify judgment rows with evidence sentences + Verify/Flag
 *  3. SIGN-OFF — reviewer + justification (only when block-tier remains)
 *
 * On confirm: PATCHes changed fields FIRST (via onPatch), then resolves with
 * proceed. PATCH failure surfaces inline — the dialog stays open and preserves
 * the reviewer's typed text. Nothing is ever un-overridable.
 *
 * Controlled component (open / onGoBack / onPublishAnyway); the promise wiring
 * lives in PrePublishGateProvider.
 */

import React from 'react';
import { AlertCircle, AlertTriangle, Info, ChevronDown, ChevronUp, Check, Flag, Loader2 } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
} from '@/components/ui/alert-dialog';
import {
  type EditableFieldValues,
  type GateEvalContext,
  type GateItem,
} from '@/lib/prePublishGate';
import type { MetadataViolation, MetadataTier } from '@/lib/metadataCriteria';
import { evaluateMetadataCriteria } from '@/lib/metadataCriteria';
import type { IssueCategory } from '@/lib/issueSummary';
import type { HumanVerifyItem } from '@/lib/humanVerify';
import { MetadataFixRow, getRuleHint } from './MetadataFixRow';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface PrePublishWarningDialogProps {
  open: boolean;
  items: GateItem[];
  defaultReviewer?: string;
  /** Reject: user clicked "Cancel" or dismissed the dialog. */
  onGoBack: () => void;
  /** Proceed: user clicked the primary publish button. Called AFTER successful PATCH. */
  onPublishAnyway: (details?: { justification?: string; reviewer?: string }) => void;
  /** PATCH editable field changes for one article. Return false/throw to signal failure. */
  onPatch?: (contentId: string, fields: EditableFieldValues) => Promise<boolean>;
  /** Resolve a human-verify item. Return false/throw to signal failure. */
  onResolveHumanVerify?: (
    contentId: string,
    itemId: string,
    status: 'verified' | 'dismissed',
    note?: string,
  ) => Promise<boolean>;
  /** Tenant ID for the per-field suggest endpoint. */
  tenantId?: string;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/**
 * Fields the FIX section can edit inline. Widened to include the full
 * SUGGESTIBLE_FIX_FIELDS set so OG / Twitter / excerpt rows get their own
 * editable input + Suggest button when they have violations.
 *
 * url_slug stays editable (but not suggestible — the backend does not support
 * slug suggestions). excerpt is included for completeness even though no
 * contract rule currently fires on it; if a rule is added later the row will
 * already be wired.
 */
const EDITABLE_FIELDS = [
  'meta_title',
  'meta_description',
  'url_slug',
  'title',
  'og_title',
  'og_description',
  'twitter_title',
  'twitter_description',
  'excerpt',
] as const;
const EDITABLE_SET = new Set<string>(EDITABLE_FIELDS);

// ---------------------------------------------------------------------------
// Category grouping (Session C — group violations by TYPE)
// ---------------------------------------------------------------------------

const CATEGORY_LABELS: Record<IssueCategory, string> = {
  pipeline: 'Pipeline',
  human: 'Sign-off',
  uniqueness: 'Uniqueness',
  contract: 'Contract',
  accessibility: 'Accessibility',
  provenance: 'Needs review',
};

/**
 * Canonical ordering for categories at the same blocking level.
 * Blocking-first means categories containing block-tier violations are
 * rendered before categories with only warn/info.
 */
const CATEGORY_ORDER: IssueCategory[] = [
  'pipeline',
  'human',
  'uniqueness',
  'contract',
  'accessibility',
  'provenance',
];

/**
 * Derive the IssueCategory for a violation. Prefers the explicit
 * `violation.category` field (set by Session A's buildGateItem). Falls back to
 * deriving from ruleId/field when the category is absent (back-compat for
 * violations that bypass normalizeViolation or come from older callers).
 */
function deriveCategory(violation: MetadataViolation): IssueCategory {
  if (violation.category) return violation.category;
  const { ruleId, field } = violation;
  if (field === 'human_qa' || field === 'human_verify' || ruleId.startsWith('human_')) {
    return 'human';
  }
  if (field === 'status' || ruleId.startsWith('backend_')) {
    return 'pipeline';
  }
  if (ruleId === 'slug_collision') {
    return 'uniqueness';
  }
  if (ruleId.startsWith('provenance_')) {
    return 'provenance';
  }
  if (ruleId.startsWith('image_') || ruleId.startsWith('missing_alt') || field === 'images') {
    return 'accessibility';
  }
  return 'contract';
}

function tierOf(violation: MetadataViolation): MetadataTier {
  return violation.tier ?? violation.severity;
}

// ---------------------------------------------------------------------------
// Shared primitives (reused from the existing dialog, coral/teal tokens only)
// ---------------------------------------------------------------------------

export function MetadataViolationChip({ violation }: { violation: MetadataViolation }) {
  const tier = tierOf(violation);
  const isBlock = tier === 'block';
  const isWarn = tier === 'warn';
  const hint = getRuleHint(violation.ruleId);
  return (
    <li
      className={
        isBlock
          ? 'flex items-start gap-1.5 rounded-md bg-coral/10 px-2 py-1 text-xs text-coral-dark'
          : isWarn
          ? 'flex items-start gap-1.5 rounded-md bg-coral-light px-2 py-1 text-xs text-coral-dark'
          : 'flex items-start gap-1.5 rounded-md bg-teal-light px-2 py-1 text-xs text-teal-dark'
      }
    >
      {isBlock ? (
        <AlertCircle size={13} className="mt-0.5 shrink-0 text-coral-dark" />
      ) : isWarn ? (
        <AlertTriangle size={13} className="mt-0.5 shrink-0 text-coral" />
      ) : (
        <Info size={13} className="mt-0.5 shrink-0 text-teal-dark" />
      )}
      <span>
        <span>{violation.message}</span>
        {hint && (
          <span className="mt-0.5 block text-xs italic text-muted-foreground">
            {hint}
          </span>
        )}
      </span>
    </li>
  );
}

/**
 * CategoryGroups — renders non-editable violations grouped by IssueCategory
 * (the Session C "group by TYPE" requirement). Each category gets a labeled
 * section header; sections are ordered blocking-first (categories with any
 * block-tier violation come before warn/info-only categories).
 *
 * Replaces the old ViolationGroups which grouped by field. The field is still
 * visible in each chip's message text.
 */
function CategoryGroups({ violations }: { violations: MetadataViolation[] }) {
  const groups = new Map<IssueCategory, MetadataViolation[]>();
  for (const v of violations) {
    const cat = deriveCategory(v);
    const arr = groups.get(cat);
    if (arr) {
      arr.push(v);
    } else {
      groups.set(cat, [v]);
    }
  }

  const sorted = [...groups.entries()].sort((a, b) => {
    const aBlock = a[1].some((v) => tierOf(v) === 'block');
    const bBlock = b[1].some((v) => tierOf(v) === 'block');
    if (aBlock !== bBlock) return aBlock ? -1 : 1;
    return CATEGORY_ORDER.indexOf(a[0]) - CATEGORY_ORDER.indexOf(b[0]);
  });

  return (
    <div className="space-y-2">
      {sorted.map(([category, groupViolations]) => (
        <div key={category}>
          <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            {CATEGORY_LABELS[category]}
          </div>
          <ul className="space-y-1">
            {groupViolations.map((v, i) => (
              <MetadataViolationChip key={`${v.ruleId}-${i}`} violation={v} />
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Section wrapper
// ---------------------------------------------------------------------------

function Section({
  title,
  hint,
  children,
}: {
  title: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="mb-3">
      <div className="mb-1.5 flex items-baseline gap-2">
        <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{title}</h4>
        {hint && <span className="text-xs text-muted-foreground">{hint}</span>}
      </div>
      {children}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Evidence disclosure — renders structured evidence as a small table/list
// ---------------------------------------------------------------------------

function formatScalar(value: unknown): string {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (Array.isArray(value)) return value.length > 0 ? `${value.length} item(s)` : 'none';
  if (typeof value === 'object') return '{…}';
  return String(value);
}

function EvidenceTable({ title, rows }: { title: string; rows: Record<string, unknown>[] }) {
  const headers = Array.from(new Set(rows.flatMap((r) => Object.keys(r)))).slice(0, 5);
  return (
    <div>
      <div className="mb-0.5 text-xs font-medium text-muted-foreground">{title.replace(/_/g, ' ')}</div>
      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-xs">
          <thead>
            <tr>
              {headers.map((h) => (
                <th
                  key={h}
                  className="border border-border bg-muted/40 px-1.5 py-0.5 text-left font-medium text-muted-foreground"
                >
                  {h.replace(/_/g, ' ')}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr key={i}>
                {headers.map((h) => (
                  <td key={h} className="max-w-[200px] truncate border border-border px-1.5 py-0.5 text-foreground" title={formatScalar(row[h])}>
                    {formatScalar(row[h])}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function EvidenceDetail({ evidence }: { evidence?: Record<string, unknown> }) {
  if (!evidence || Object.keys(evidence).length === 0) return null;
  return (
    <div className="mt-2 space-y-1.5 rounded-md bg-muted/40 p-2">
      {Object.entries(evidence).map(([key, value]) => {
        if (Array.isArray(value) && value.length > 0 && typeof value[0] === 'object' && value[0] !== null) {
          return <EvidenceTable key={key} title={key} rows={value as Record<string, unknown>[]} />;
        }
        return (
          <div key={key} className="flex gap-1.5 text-xs">
            <span className="shrink-0 font-medium text-muted-foreground">{key.replace(/_/g, ' ')}:</span>
            <span className="text-muted-foreground">{formatScalar(value)}</span>
          </div>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// ConfirmRow — one human-verify judgment row
// ---------------------------------------------------------------------------

function ConfirmRow({
  item,
  contentId,
  resolveState,
  onResolve,
}: {
  item: HumanVerifyItem;
  contentId: string;
  resolveState: 'idle' | 'loading' | 'error';
  onResolve: (contentId: string, itemId: string, status: 'verified' | 'dismissed', note?: string) => void;
}) {
  const [expanded, setExpanded] = React.useState(false);
  const [noteOpen, setNoteOpen] = React.useState(false);
  const [note, setNote] = React.useState('');
  const hasEvidence = !!item.evidence && Object.keys(item.evidence).length > 0;
  const isLoading = resolveState === 'loading';

  return (
    <div className="rounded-md border border-border bg-card p-3">
      {/* Prompt + evidence sentence */}
      <div className="text-sm font-medium text-foreground">{item.prompt}</div>
      {item.machine_context && (
        <p className="mt-0.5 text-xs text-muted-foreground">{item.machine_context}</p>
      )}

      {/* See details disclosure */}
      {hasEvidence && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          aria-expanded={expanded}
          className="mt-1 inline-flex items-center gap-0.5 text-xs text-teal-dark hover:underline focus:outline-none focus-visible:underline"
        >
          {expanded ? <ChevronUp size={11} /> : <ChevronDown size={11} />}
          {expanded ? 'Hide details' : 'See details'}
        </button>
      )}
      {expanded && hasEvidence && <EvidenceDetail evidence={item.evidence} />}

      {/* Actions */}
      <div className="mt-2 flex flex-wrap items-center gap-2">
        <button
          type="button"
          disabled={isLoading}
          onClick={() => onResolve(contentId, item.id, 'verified', note.trim() || undefined)}
          className="inline-flex items-center gap-1 rounded-md border border-teal/30 bg-teal-light px-2.5 py-1 text-xs font-medium text-teal-dark hover:bg-teal/10 disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-teal/30"
        >
          {isLoading ? <Loader2 size={11} className="animate-spin" /> : <Check size={11} />}
          Verify
        </button>
        <button
          type="button"
          disabled={isLoading}
          onClick={() => onResolve(contentId, item.id, 'dismissed', note.trim() || undefined)}
          className="inline-flex items-center gap-1 rounded-md border border-coral/30 bg-coral-light px-2.5 py-1 text-xs font-medium text-coral-dark hover:bg-coral/10 disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-coral/30"
        >
          <Flag size={11} />
          Flag
        </button>
        <button
          type="button"
          onClick={() => setNoteOpen((v) => !v)}
          className="text-xs text-muted-foreground hover:text-muted-foreground hover:underline focus:outline-none focus-visible:underline"
        >
          {noteOpen ? 'Cancel note' : 'Add note'}
        </button>
        {resolveState === 'error' && (
          <span className="text-xs text-coral-dark">Resolution failed — try again.</span>
        )}
      </div>
      {noteOpen && (
        <input
          type="text"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Optional note"
          className="mt-1.5 block w-full rounded-md border border-border px-2.5 py-1 text-xs text-foreground outline-none focus:border-teal"
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main dialog
// ---------------------------------------------------------------------------

export const PrePublishWarningDialog: React.FC<PrePublishWarningDialogProps> = ({
  open,
  items,
  defaultReviewer,
  onGoBack,
  onPublishAnyway,
  onPatch,
  onResolveHumanVerify,
  tenantId,
}) => {
  const [editedValues, setEditedValues] = React.useState<Record<string, EditableFieldValues>>({});
  const [resolvedIds, setResolvedIds] = React.useState<Set<string>>(new Set());
  const [justification, setJustification] = React.useState('');
  const [reviewer, setReviewer] = React.useState(defaultReviewer ?? '');
  const [patchError, setPatchError] = React.useState<string | null>(null);
  const [patching, setPatching] = React.useState(false);
  const [resolveStates, setResolveStates] = React.useState<Record<string, 'loading' | 'error'>>({});

  React.useEffect(() => {
    if (!open) return;
    setEditedValues({});
    setResolvedIds(new Set());
    setJustification('');
    setReviewer(defaultReviewer ?? '');
    setPatchError(null);
    setPatching(false);
    setResolveStates({});
  }, [defaultReviewer, open]);

  // ---- Per-article live computation ----------------------------------------
  const articleData = React.useMemo(() => {
    return items.map((item) => {
      const cid = item.contentId ?? '';
      const fv = item.fieldValues ?? {};
      const ctx: GateEvalContext = item.evalContext ?? {};
      // Build initial values for ALL editable fields. The original 4 come from
      // fieldValues; OG/Twitter come from evalContext; excerpt has no gate-side
      // source (defaults to empty — suggest-only until a rule fires on it).
      const initial: Record<string, string | null | undefined> = {
        meta_title: fv.meta_title,
        meta_description: fv.meta_description,
        url_slug: fv.url_slug,
        title: fv.title,
        og_title: ctx.ogTitle,
        og_description: ctx.ogDescription,
        twitter_title: ctx.twitterTitle,
        twitter_description: ctx.twitterDescription,
        excerpt: '',
      };
      const edits = (editedValues[cid] ?? {}) as Record<string, string | undefined>;
      const current: Record<string, string> = {};
      for (const f of EDITABLE_FIELDS) {
        const e = edits[f];
        current[f] = e !== undefined ? e : (initial[f] ?? '');
      }

      // Fail-soft: a throw inside the evaluator must not tear down the dialog
      // mid-publish. Fall back to the violations the gate already computed.
      let liveViolations: MetadataViolation[];
      try {
        liveViolations = evaluateMetadataCriteria({
          metaTitle: current.meta_title || null,
          metaDescription: current.meta_description || null,
          title: current.title || null,
          urlSlug: current.url_slug || null,
          keyword: ctx.keyword,
          brandName: ctx.brandName,
          focusKeyword: ctx.focusKeyword,
          ogTitle: current.og_title || null,
          ogDescription: current.og_description || null,
          twitterTitle: current.twitter_title || null,
          twitterDescription: current.twitter_description || null,
        });
      } catch {
        liveViolations = item.violations;
      }

      // Fields to show in FIX section: union of originally-flagged + live-flagged
      const showFixFields = new Set<string>();
      for (const v of item.violations) {
        if (EDITABLE_SET.has(v.field)) showFixFields.add(v.field);
      }
      for (const v of liveViolations) {
        if (EDITABLE_SET.has(v.field)) showFixFields.add(v.field);
      }

      // Live violations keyed by editable field
      const fixViolationsByField: Record<string, MetadataViolation[]> = {};
      const otherLive: MetadataViolation[] = [];
      for (const v of liveViolations) {
        if (EDITABLE_SET.has(v.field)) {
          (fixViolationsByField[v.field] ??= []).push(v);
        } else {
          otherLive.push(v);
        }
      }

      // Structural violations (can't be fixed in-dialog; from original)
      const structural = item.violations.filter(
        (v) => v.field === 'human_qa' || v.field === 'status',
      );

      // Non-editable + structural for chip display
      const otherDisplay = [...otherLive, ...structural];

      // Unresolved human-verify items
      const confirmItems = (item.humanVerifyItems ?? []).filter(
        (hv) => !resolvedIds.has(hv.id),
      );

      // Has edits for this article?
      const hasEdits = EDITABLE_FIELDS.some((f) => {
        const e = edits[f];
        return e !== undefined && e !== (initial[f] ?? '');
      });

      return {
        item,
        cid,
        current,
        showFixFields,
        fixViolationsByField,
        otherDisplay,
        confirmItems,
        hasEdits,
      };
    });
  }, [items, editedValues, resolvedIds]);

  // ---- Aggregate counts ----------------------------------------------------
  const totalHasEdits = articleData.some((a) => a.hasEdits);
  const remainingBlock = articleData.reduce((sum, a) => {
    const fixBlock = Object.values(a.fixViolationsByField)
      .flat()
      .filter((v) => tierOf(v) === 'block').length;
    const otherBlock = a.otherDisplay.filter((v) => tierOf(v) === 'block').length;
    const hvBlock = a.confirmItems.filter((hv) => hv.severity === 'block').length;
    return sum + fixBlock + otherBlock + hvBlock;
  }, 0);
  const remainingWarn = articleData.reduce((sum, a) => {
    const fixWarn = Object.values(a.fixViolationsByField)
      .flat()
      .filter((v) => tierOf(v) === 'warn').length;
    const otherWarn = a.otherDisplay.filter((v) => tierOf(v) === 'warn').length;
    const hvWarn = a.confirmItems.filter((hv) => hv.severity === 'warn').length;
    return sum + fixWarn + otherWarn + hvWarn;
  }, 0);
  const remainingIssues = remainingBlock + remainingWarn;
  const needsSignOff = remainingBlock > 0;
  const canPublish =
    !needsSignOff || (justification.trim().length > 0 && reviewer.trim().length > 0);

  // ---- Handlers ------------------------------------------------------------
  const setFieldValue = React.useCallback(
    (cid: string, field: string, value: string) => {
      setEditedValues((prev) => ({
        ...prev,
        [cid]: { ...prev[cid], [field]: value },
      }));
    },
    [],
  );

  const handleResolve = React.useCallback(
    async (cid: string, itemId: string, status: 'verified' | 'dismissed', note?: string) => {
      if (!onResolveHumanVerify) return;
      setResolveStates((prev) => ({ ...prev, [itemId]: 'loading' }));
      try {
        const ok = await onResolveHumanVerify(cid, itemId, status, note);
        if (ok) {
          setResolvedIds((prev) => new Set(prev).add(itemId));
          setResolveStates((prev) => {
            const next = { ...prev };
            delete next[itemId];
            return next;
          });
        } else {
          setResolveStates((prev) => ({ ...prev, [itemId]: 'error' }));
        }
      } catch {
        setResolveStates((prev) => ({ ...prev, [itemId]: 'error' }));
      }
    },
    [onResolveHumanVerify],
  );

  const handlePrimary = React.useCallback(async (options?: { skipPatch?: boolean }) => {
    setPatchError(null);

    if (totalHasEdits && onPatch && !options?.skipPatch) {
      setPatching(true);
      for (const a of articleData) {
        if (!a.hasEdits || !a.cid) continue;
        const fv = a.item.fieldValues ?? {};
        const ctx = a.item.evalContext ?? {};
        // Rebuild the same initial map used in the live computation so the
        // "what changed?" diff matches what the reviewer sees.
        const initial: Record<string, string | null | undefined> = {
          meta_title: fv.meta_title,
          meta_description: fv.meta_description,
          url_slug: fv.url_slug,
          title: fv.title,
          og_title: ctx.ogTitle,
          og_description: ctx.ogDescription,
          twitter_title: ctx.twitterTitle,
          twitter_description: ctx.twitterDescription,
          excerpt: '',
        };
        const edits = (editedValues[a.cid] ?? {}) as Record<string, string | undefined>;
        const changed: Record<string, string> = {};
        for (const f of EDITABLE_FIELDS) {
          const e = edits[f];
          if (e !== undefined && e !== (initial[f] ?? '')) {
            changed[f] = e;
          }
        }
        if (Object.keys(changed).length === 0) continue;
        try {
          // patchContent accepts the full field set (og_title, og_description,
          // twitter_title, twitter_description, excerpt, etc.) even though
          // EditableFieldValues only declares the original four. The extra
          // keys flow through to the backend PATCH body unchanged.
          const ok = await onPatch(a.cid, changed as EditableFieldValues);
          if (!ok) {
            setPatchError(
              'Could not save your metadata changes. Your edits are kept — try again, or publish the article as it stands.',
            );
            setPatching(false);
            return;
          }
        } catch {
          setPatchError(
            'Could not save your metadata changes. Your edits are kept — try again, or publish the article as it stands.',
          );
          setPatching(false);
          return;
        }
      }
      setPatching(false);
    }

    onPublishAnyway(
      needsSignOff
        ? { justification: justification.trim(), reviewer: reviewer.trim() }
        : undefined,
    );
  }, [articleData, editedValues, needsSignOff, justification, reviewer, onPatch, onPublishAnyway, totalHasEdits]);

  // ---- Button label + color ------------------------------------------------
  const primaryLabel =
    remainingIssues > 0
      ? `${totalHasEdits ? 'Save changes and p' : 'P'}ublish anyway`
      : totalHasEdits
        ? 'Save changes and publish'
        : 'Publish';
  const primaryIsCoral = remainingIssues > 0;

  return (
    <AlertDialog
      open={open}
      onOpenChange={(next) => {
        if (!next && !patching) onGoBack();
      }}
    >
      <AlertDialogContent className="flex max-h-[85vh] flex-col overflow-hidden p-0">
        {/* Header — pinned */}
        <AlertDialogHeader className="shrink-0 border-b border-border px-5 pt-5 pb-3">
          <AlertDialogTitle className="flex items-center gap-2">
            {needsSignOff ? (
              <AlertCircle size={20} className="text-coral-dark" />
            ) : remainingWarn > 0 ? (
              <AlertTriangle size={20} className="text-coral" />
            ) : (
              <Info size={20} className="text-teal-dark" />
            )}
            {needsSignOff
              ? 'Sign-off required to publish'
              : remainingWarn > 0
                ? 'Review before publishing'
                : 'Ready to publish'}
          </AlertDialogTitle>
          <AlertDialogDescription className="mt-1 text-sm text-muted-foreground">
            {items.length === 1
              ? 'Fix what you can here, confirm what needs a human check, then publish.'
              : `${items.length} articles need review before publishing.`}
          </AlertDialogDescription>
        </AlertDialogHeader>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto px-5 py-4">
          {patchError && (
            <div className="mb-3 rounded-md border border-coral/30 bg-coral-light px-3 py-2 text-sm text-coral-dark">
              <p>{patchError}</p>
              <button
                type="button"
                className="mt-1.5 text-sm font-medium underline underline-offset-2 hover:text-coral focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                onClick={() => handlePrimary({ skipPatch: true })}
                disabled={patching}
              >
                Publish as it stands
              </button>
            </div>
          )}

          {articleData.map((a) => {
            const hasFixRows = a.showFixFields.size > 0;
            const hasConfirm = a.confirmItems.length > 0;
            const hasOther = a.otherDisplay.length > 0;
            if (!hasFixRows && !hasConfirm && !hasOther) return null;

            return (
              <div key={a.cid || a.item.title} className="mb-4 last:mb-0">
                {items.length > 1 && (
                  <div className="mb-2 truncate text-sm font-semibold text-foreground">
                    {a.item.title || a.cid || 'Untitled article'}
                  </div>
                )}

                {/* FIX section */}
                {hasFixRows && (
                  <Section title="Fix" hint="Editable — changes save on publish">
                    <div className="space-y-2">
                      {EDITABLE_FIELDS.filter((f) => a.showFixFields.has(f)).map((field) => (
                        <MetadataFixRow
                          key={field}
                          field={field}
                          value={a.current[field] ?? ''}
                          violations={a.fixViolationsByField[field] ?? []}
                          onChange={(val) => a.cid && setFieldValue(a.cid, field, val)}
                          contentId={a.cid}
                          tenantId={tenantId}
                          suggestDisabled={a.item.metadataGenerating ?? false}
                        />
                      ))}
                    </div>
                  </Section>
                )}

                {/* Non-editable violations */}
                {hasOther && (
                  <Section title={hasFixRows ? 'Also review' : 'Review'}>
                    <div className="rounded-md border border-border bg-warm-bg p-3">
                      <CategoryGroups violations={a.otherDisplay} />
                    </div>
                  </Section>
                )}

                {/* CONFIRM section */}
                {hasConfirm && (
                  <Section
                    title="Confirm"
                    hint={`${a.confirmItems.length} item${a.confirmItems.length === 1 ? '' : 's'} need human review`}
                  >
                    <div className="space-y-2">
                      {a.confirmItems.map((hv) => (
                        <ConfirmRow
                          key={hv.id}
                          item={hv}
                          contentId={a.cid}
                          resolveState={resolveStates[hv.id] ?? 'idle'}
                          onResolve={handleResolve}
                        />
                      ))}
                    </div>
                  </Section>
                )}
              </div>
            );
          })}

          {/* SIGN-OFF section */}
          {needsSignOff && (
            <div className="rounded-md border border-coral/30 bg-card p-3">
              <div className="mb-1 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-coral-dark">
                <AlertCircle size={13} />
                Sign-off required
              </div>
              <p className="mb-2 text-xs text-muted-foreground">
                {remainingBlock} block-tier{' '}
                {remainingBlock === 1 ? 'item' : 'items'}{' '}
                {remainingBlock === 1 ? 'remains' : 'remain'}. Provide your name and a
                justification to publish with override.
              </p>
              <label className="block text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Reviewer
                <input
                  value={reviewer}
                  onChange={(e) => setReviewer(e.target.value)}
                  className="mt-1 block w-full rounded-md border border-border px-3 py-2 text-sm font-normal normal-case tracking-normal text-foreground outline-none focus:border-coral"
                  placeholder="Reviewer name or email"
                />
              </label>
              <label className="mt-3 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Justification
                <textarea
                  value={justification}
                  onChange={(e) => setJustification(e.target.value)}
                  className="mt-1 block min-h-20 w-full rounded-md border border-border px-3 py-2 text-sm font-normal normal-case tracking-normal text-foreground outline-none focus:border-coral"
                  placeholder="Why is this acceptable to publish now?"
                />
              </label>
            </div>
          )}
        </div>

        {/* Footer — pinned */}
        {/* sm:justify-between beats AlertDialogFooter's default sm:justify-end,
            which would otherwise pull Cancel next to Publish on wide screens. */}
        <AlertDialogFooter className="shrink-0 justify-between border-t border-border px-5 py-3 sm:justify-between">
          <button
            type="button"
            className="inline-flex items-center justify-center rounded-md border border-border bg-background px-4 py-2 text-sm font-medium text-foreground hover:bg-muted/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            onClick={onGoBack}
            disabled={patching}
          >
            Cancel
          </button>
          <button
            type="button"
            className={
              !canPublish || patching
                ? 'inline-flex cursor-not-allowed items-center justify-center rounded-md bg-muted px-4 py-2 text-sm font-medium text-white'
                : primaryIsCoral
                  ? 'inline-flex items-center justify-center rounded-md bg-coral px-4 py-2 text-sm font-medium text-white hover:bg-coral-hover focus:outline-none focus-visible:ring-2 focus-visible:ring-coral/40'
                  : 'inline-flex items-center justify-center rounded-md bg-teal-dark px-4 py-2 text-sm font-medium text-white hover:bg-teal focus:outline-none focus-visible:ring-2 focus-visible:ring-teal/40'
            }
            disabled={!canPublish || patching}
            onClick={() => handlePrimary()}
          >
            {patching ? (
              <>
                <Loader2 size={14} className="mr-1.5 animate-spin" />
                Saving…
              </>
            ) : (
              primaryLabel
            )}
          </button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default PrePublishWarningDialog;
